// Build revenue tracker + builds on the calendar (Oct 27, 2026).
//
// Greg: "i want alex to see revenues per month based on build date. he should
// have a tracker on his home page showing scoped revenue, scheduled revenue and
// built revenue" — then "that might be good on all their home boards
// individually and company wide, they can toggle back and forth". And on the
// crew board goals: "scheduled builds should show the number of builds
// scheduled for that particular week, not the number of builds that got
// scheduled that week."
//
// Both numbers come from DEFINER RPCs (migration 20261027f) because the deals
// RLS shows an RM only own + split deals, and an RM's "company" view must still
// add up. They return sums and counts only.
//
//   built      → build_completed_date in the month
//   scheduled  → build_scheduled_date in the month, not built yet
//   scoped     → scope in, no build date yet; month = the EXPECTED build date,
//                which is deals.ho_preferred_build_date (Greg: "it would be the
//                preferred build date essentially"). The homeowner picks it at
//                signing; Alex or the RM moves it on the Build Calendar.
//
// Supplements are excluded everywhere (field roles never see supplement
// dollars). The per-job $ rule lives in fn_deal_build_value(); prebuildValue()
// below is its client mirror for the expected-builds list. Change both together.

import { supabase } from "./supabase";
import { calcScope } from "./scopeCalc";

export type RevenueScope = "mine" | "company";

export interface RevenueMonth {
  ym: string;
  built: number;
  builtN: number;
  scheduled: number;
  scheduledN: number;
  scoped: number;
  scopedN: number;
}

export interface BuildRevenue {
  scope: RevenueScope;
  /** ET calendar day the server counted from. */
  today: string;
  months: RevenueMonth[];
  /** Scoped jobs with no expected (preferred) build date yet. */
  noDate: { scoped: number; n: number };
  /** On the calendar for a day already gone, never marked built. */
  pastDue: { scheduled: number; n: number };
}

const num = (v: unknown) => {
  const n = Number(v ?? 0);
  return Number.isFinite(n) ? n : 0;
};

export async function fetchBuildRevenue(scope: RevenueScope, back = 23, ahead = 6): Promise<BuildRevenue> {
  const { data, error } = await supabase.rpc("build_revenue_months", {
    p_scope: scope,
    p_back: back,
    p_ahead: ahead,
  });
  if (error) throw error;
  const d = (data ?? {}) as Record<string, unknown>;
  const months = Array.isArray(d.months) ? (d.months as Array<Record<string, unknown>>) : [];
  const nd = (d.no_date ?? {}) as Record<string, unknown>;
  const pd = (d.past_due ?? {}) as Record<string, unknown>;
  return {
    scope: d.scope === "mine" ? "mine" : "company",
    today: String(d.today ?? ""),
    months: months.map((m) => ({
      ym: String(m.ym),
      built: num(m.built),
      builtN: num(m.built_n),
      scheduled: num(m.scheduled),
      scheduledN: num(m.scheduled_n),
      scoped: num(m.scoped),
      scopedN: num(m.scoped_n),
    })),
    noDate: { scoped: num(nd.scoped), n: num(nd.scoped_n) },
    pastDue: { scheduled: num(pd.scheduled), n: num(pd.scheduled_n) },
  };
}

/**
 * Builds whose FIRST build day falls in [fromISO, toISO), per person — owner 1,
 * or 0.5 each on a split. This is the "Builds" goal actual (crew board week,
 * home goal cards, the month build race, the goals audit).
 */
export async function fetchCalendarBuilds(fromISO: string, toISO: string): Promise<Record<string, number>> {
  const { data, error } = await supabase.rpc("crew_calendar_builds", { p_from: fromISO, p_to: toISO });
  if (error) throw error;
  const out: Record<string, number> = {};
  for (const r of (Array.isArray(data) ? data : []) as Array<Record<string, unknown>>) {
    if (r.user_id) out[String(r.user_id)] = num(r.builds);
  }
  return out;
}

/** Stages where a job has a scope and waits on a build date. Mirrors build_revenue_months. */
export const SCOPED_STAGES = ["install_meeting", "build_submitted", "schedule_build", "build_scheduled"] as const;

/** Columns calcScope needs, for selects that feed prebuildValue(). The trade
 *  money columns (Oct 29, 2026; migration 20261029f) ride along: a trade billed
 *  to the homeowner is in the invoice, so it is in the build value, as SQL
 *  fn_deal_build_value has it through fn_deal_invoice_gross. */
export const BUILD_VALUE_COLS =
  "rcv_total,depreciation_total,deductible,pwi_code,upgrades,retail_profit,at_cost_retail," +
  "acv_credit,dep_credit,misc_credit,non_commissionable,acv_only,is_retail,retail_contract_total," +
  "items_not_performing_lines,punch_list_items," +
  "trade_ho_total,trade_ho_floor,trade_margin_loss,trade_ho_lines";

/**
 * Build value of a job that isn't built yet: the contract with supplements
 * removed, minus non-collect revenue. Mirror of fn_deal_build_value()'s
 * pre-build branch. The trades billed to the homeowner are in it (Oct 29,
 * 2026): calcScope's initialInvoice and SQL fn_deal_invoice_gross both carry
 * trade_ho_total.
 */
export function prebuildValue(d: Record<string, unknown>): number {
  const c = calcScope({
    ...d,
    supplements: 0,
    supplement_invoice_amount: 0,
    supplement_approved_amount: 0,
    final_invoice_amount: null,
  });
  return Math.max(0, c.initialInvoice - c.nonCollectRevenue);
}

export const fmtMoney = (n: number) =>
  n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(2)}M`
  : n >= 10_000 ? `$${Math.round(n / 1_000)}K`
  : n >= 1_000 ? `$${(n / 1_000).toFixed(1)}K`
  : `$${Math.round(n)}`;

export const fmtJobs = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(1));

export const monthShort = (ym: string) =>
  new Date(Number(ym.slice(0, 4)), Number(ym.slice(5, 7)) - 1, 1).toLocaleDateString("en-US", { month: "short" });

export const monthLong = (ym: string) =>
  new Date(Number(ym.slice(0, 4)), Number(ym.slice(5, 7)) - 1, 1).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });

// ── Build Calendar (Alex's expected-build list) ───────────────────────────

export type CalKind = "built" | "scheduled" | "past_due" | "expected" | "needs_date";

export interface CalJob {
  /** null when the viewer can't open this job (another rep's, for an RM). The
   *  name, street and location still show (Oct 29, 2026). */
  dealId: string | null;
  kind: CalKind;
  /** First build day (scheduled) or the expected/preferred day; null = needs a date. */
  day: string | null;
  /** Last day of a multi-day build. */
  endDay: string | null;
  stage: string;
  city: string | null;
  zip: string | null;
  street: string | null;
  homeowner: string | null;
  value: number | null;
  /** Exact for everyone since Oct 29, 2026 (was rounded on jobs the viewer can't open). */
  lat: number | null;
  lon: number | null;
  /** Expected squares; see squaresSource for where the number came from. */
  squares: number | null;
  /** eagleview = PM sheet squares with waste; entered = deals.total_squares;
   *  retail = retail_squares; estimate = county parcel roof estimate (rough). */
  squaresSource: "eagleview" | "entered" | "retail" | "estimate" | null;
  rep: string;
  splitRep: string | null;
  mine: boolean;
  canEdit: boolean;
}

export async function fetchCalendarJobs(fromISO: string, toISO: string): Promise<{ today: string; jobs: CalJob[] }> {
  const { data, error } = await supabase.rpc("build_calendar_jobs", { p_from: fromISO, p_to: toISO });
  if (error) throw error;
  const d = (data ?? {}) as Record<string, unknown>;
  const rows = Array.isArray(d.jobs) ? (d.jobs as Array<Record<string, unknown>>) : [];
  const str = (v: unknown) => (v == null || v === "" ? null : String(v));
  const optNum = (v: unknown) => (v == null || v === "" ? null : Number.isFinite(Number(v)) ? Number(v) : null);
  return {
    today: String(d.today ?? ""),
    jobs: rows.map((r) => ({
      dealId: str(r.deal_id),
      kind: String(r.kind) as CalKind,
      day: str(r.day),
      endDay: str(r.end_day),
      stage: String(r.stage ?? ""),
      city: str(r.city),
      zip: str(r.zip),
      street: str(r.street),
      homeowner: str(r.homeowner),
      value: optNum(r.value),
      lat: optNum(r.lat),
      lon: optNum(r.lon),
      squares: optNum(r.squares),
      squaresSource: (["eagleview", "entered", "retail", "estimate"].includes(String(r.squares_source))
        ? String(r.squares_source)
        : null) as CalJob["squaresSource"],
      rep: String(r.rep ?? "Unassigned"),
      splitRep: str(r.split_rep),
      mine: r.mine === true,
      canEdit: r.can_edit === true,
    })),
  };
}

/** Straight-line miles between two points. */
export function milesBetween(aLat: number, aLon: number, bLat: number, bLon: number): number {
  const R = 3958.8;
  const toRad = (x: number) => (x * Math.PI) / 180;
  const dLat = toRad(bLat - aLat);
  const dLon = toRad(bLon - aLon);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * Rough drive minutes from straight-line miles: roads run ~1.3× the crow-fly
 * distance and suburban driving averages ~30 mph, so 1 mile ≈ 2.6 minutes.
 * An estimate for pairing jobs, not a route.
 */
export const driveMinutes = (miles: number) => Math.max(1, Math.round(miles * 2.6));

/** Greg: "within 5-10 minutes away … ramiro would be able to pm both." */
export const PAIR_MAX_MINUTES = 10;

export function minutesBetween(a: CalJob, b: CalJob): number | null {
  if (a.lat == null || a.lon == null || b.lat == null || b.lon == null) return null;
  return driveMinutes(milesBetween(a.lat, a.lon, b.lat, b.lon));
}

/** Does a job (scheduled, possibly multi-day) cover this ET day? */
export function coversDay(j: CalJob, day: string): boolean {
  if (!j.day) return false;
  const end = j.endDay && j.endDay > j.day ? j.endDay : j.day;
  return day >= j.day && day <= end;
}

/**
 * Set a job's expected (preferred) build date. The deals UPDATE policies decide
 * who may: an RM on their own job, the ASM and leadership on any. A write RLS
 * refuses comes back as zero rows, so that is checked and named.
 */
export async function setExpectedBuildDate(
  dealId: string,
  day: string | null,
  actor: { id: string; name: string } | null,
  label: string,
): Promise<void> {
  const { data, error } = await supabase
    .from("deals")
    .update({ ho_preferred_build_date: day })
    .eq("id", dealId)
    .select("id, owner_id, split_rm_id, homeowner_name");
  if (error) throw error;
  const row = (data ?? [])[0] as
    | { id: string; owner_id: string | null; split_rm_id: string | null; homeowner_name: string | null }
    | undefined;
  if (!row) throw new Error("You can't change the date on this job.");

  const title = day ? `Expected build date set to ${label}` : "Expected build date cleared";
  // Best-effort trail + a heads-up to the job's reps when someone else moved it.
  await supabase
    .from("activity_log")
    .insert({
      deal_id: dealId,
      activity_type: "field_change",
      title,
      description: actor ? `${title} by ${actor.name} on the Build Calendar.` : title,
      performed_by: actor?.id ?? null,
      metadata: { source: "build_calendar", field: "ho_preferred_build_date", to_value: day },
    })
    .then(({ error: e }) => e && console.warn("activity_log insert failed", e));
  const notify = [row.owner_id, row.split_rm_id].filter(
    (id, i, a): id is string => !!id && id !== actor?.id && a.indexOf(id) === i,
  );
  if (notify.length) {
    await supabase
      .from("notifications")
      .insert(
        notify.map((uid) => ({
          user_id: uid,
          deal_id: dealId,
          type: "expected_build_set",
          title: day
            ? `Tentative build: ${row.homeowner_name ?? "your job"} · ${label}`
            : `Tentative build date cleared: ${row.homeowner_name ?? "your job"}`,
          body: actor
            ? `${actor.name} ${day ? `penciled this job in for ${label}` : "cleared the expected date"}. Robert books the real date.`
            : null,
          metadata: { url: "/build-calendar", source: "build_calendar" },
        })),
      )
      .then(({ error: e }) => e && console.warn("notifications insert failed", e));
  }
}

export const mapsHref = (j: Pick<CalJob, "street" | "city" | "zip">) =>
  `https://maps.google.com/?q=${encodeURIComponent([j.street, j.city, "GA", j.zip].filter(Boolean).join(", "))}`;

/** "34 sq", or "~31 sq" when it's the parcel estimate. */
export function fmtSquares(j: Pick<CalJob, "squares" | "squaresSource">): string | null {
  if (j.squares == null || !(j.squares > 0)) return null;
  const n = Math.round(j.squares);
  return j.squaresSource === "estimate" ? `~${n} sq` : `${n} sq`;
}

/** A build's squares spread over its days, so a two-day build isn't counted twice. */
export function squaresPerDay(j: CalJob): number {
  if (j.squares == null || !(j.squares > 0)) return 0;
  if (!j.day || !j.endDay || j.endDay <= j.day) return j.squares;
  const a = new Date(`${j.day}T12:00:00`).getTime();
  const b = new Date(`${j.endDay}T12:00:00`).getTime();
  return j.squares / (Math.round((b - a) / 86_400_000) + 1);
}

/** Squares on a day: total, and whether any of it is an estimate. */
export function daySquares(list: CalJob[]): { total: number; rough: boolean } {
  let total = 0;
  let rough = false;
  for (const j of list) {
    const sq = squaresPerDay(j);
    if (sq > 0) {
      total += sq;
      if (j.squaresSource === "estimate") rough = true;
    }
  }
  return { total, rough };
}

export const fmtDaySquares = (d: { total: number; rough: boolean }) =>
  d.total > 0 ? `${d.rough ? "~" : ""}${Math.round(d.total)} sq` : null;

/** "Jordan Franklin" → "Jordan F." */
export const repShort = (name: string | null) => {
  if (!name) return "";
  const w = name.trim().split(/\s+/);
  return w.length > 1 ? `${w[0]} ${w[w.length - 1][0]}.` : w[0];
};
